/**
 * Os DOIS formulários que a IA abre por `GlobalBus` — nova oportunidade do CRM
 * e nova tarefa ([T019] / US-5 da spec-010).
 *
 * O canal `store` da spec-001 servia a uma tela JÁ montada. Aqui o formulário é
 * um MODAL: o `$emit` no canal `MODAL.*` é o que o abre, já preenchido, para o
 * consultor conferir e clicar em "Salvar" — quem grava continua sendo ele.
 *
 * ## O contrato de campos é o do PRÓPRIO modal
 *
 * `crm/nova-oportunidade` reproduz o `item` que `crm-opportunities-modal.vue`
 * (linhas 367-503 do template, `open()` em :117) desenha campo a campo;
 * `tarefas/nova-tarefa` reproduz as chaves que `_buildCreateModeTaskData`
 * (`task-detail-modal.vue:188-219`) lê. Campo fora dessas listas é RECUSADO
 * pelo nome: passar adiante o que o modal ignora faria a tool dizer "preenchi"
 * sobre um valor que ninguém vai ver.
 *
 * ★ **O vocabulário vem dos módulos, não daqui.** `tipo_oportunidade` é
 *   validado contra `OPPORTUNITY_TYPES` e `temperatura` contra as chaves de
 *   `OPPORTUNITY_TEMPERATURE_NAMES` — as MESMAS constantes que alimentam os
 *   controles do modal. Uma lista local divergiria na primeira opção nova, e o
 *   sintoma seria a IA recusando um tipo que existe na tela.
 *
 * ★ **As duas telas guardam data em formatos DIFERENTES, e isso foi medido.**
 *   O modal do CRM escreve e lê `data_referencia` em `DD/MM/YYYY`
 *   (`setDefaultCamposCriacao`, e o `formatPayload` manda assim ao backend);
 *   o formulário de tarefa guarda ISO `YYYY-MM-DD` e converte só na borda do
 *   editor (`taskDateInput.js`, cujo cabeçalho documenta o porquê). O
 *   vocabulário do fio é ISO, então a conversão acontece AQUI, uma vez por
 *   formulário. Para a tarefa quem converte é o `toIsoDate` da própria tela —
 *   uma régua nova aceitaria o que o formulário recusa.
 */
import { MODAL } from '@constants/Text'
import { aawzDate } from '@utils/dates/aawz-date'
import {
  OPPORTUNITY_TYPES,
  OPPORTUNITY_TEMPERATURE_NAMES,
} from '@modules/crm/constants/Text.js'
import { toIsoDate } from '@modules/tarefas/utils/taskDateInput'
import {
  registrarFormulario,
  formularioRegistrado,
  CANAL_GLOBALBUS,
} from './preencher'

export const FORMULARIO_NOVA_OPORTUNIDADE = 'crm/nova-oportunidade'
export const FORMULARIO_NOVA_TAREFA = 'tarefas/nova-tarefa'

/**
 * O `external_source` das tarefas que nascem da conversa com a IA. É o mesmo
 * eixo do `atendimento_mensagem` que o chat do atendimento já usa
 * (`chat-window.vue:483`), e **não é campo do modelo**: quem diz de onde a
 * tarefa veio é o código, não o texto que a IA escreveu.
 */
export const ORIGEM_DA_IA = 'ia-crm'

/** Formato que o modal do CRM escreve e lê. */
const FORMATO_DO_CRM = 'DD/MM/YYYY'
/** Formato que o formulário de tarefa guarda. */
const FORMATO_DA_TAREFA = 'YYYY-MM-DD'

const TEMPERATURAS = Object.keys(OPPORTUNITY_TEMPERATURE_NAMES)

const ehTextoUtil = (v) => typeof v === 'string' && v.trim().length > 0

/** `typeof number` finito, ou string que representa um. `''` não é número. */
function comoNumero(valor) {
  if (typeof valor === 'number') return isFinite(valor) ? valor : null
  if (!ehTextoUtil(valor)) return null
  const n = Number(valor.trim())
  return isFinite(n) ? n : null
}

/**
 * O dia que o texto pediu, em números — `[ano, mes, dia]`. Só as duas formas
 * que `toIsoDate` aceita; qualquer outra devolve `null` e cai fora antes.
 */
function diaPedido(texto) {
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(texto)
  if (iso) return [Number(iso[1]), Number(iso[2]), Number(iso[3])]
  const br = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(texto)
  if (br) return [Number(br[3]), Number(br[2]), Number(br[1])]
  return null
}

/**
 * A régua de data é a da TELA DE TAREFAS: `toIsoDate`
 * (`tarefas/utils/taskDateInput.js`) aceita ISO e `DD/MM/YYYY`, recusa data
 * parcial e devolve ISO. Reescrevê-la aqui criaria uma segunda noção de "data
 * válida" — que aceitaria o que o formulário recusa, ou o contrário, sem nada
 * ficar vermelho.
 *
 * ★ O que ela NÃO faz, e por isso a ida-e-volta: **data impossível ROLA em
 *   silêncio**. Medido em 26/08 — `toIsoDate('2026-02-31')` devolve
 *   `'2026-03-03'`. O gate de formato do `parseTaskDate` reprova as duas
 *   tentativas, e o FALLBACK (`aawzDate(value)` -> `new Date(value)`) aceita:
 *   o parser legado do V8 transborda o dia (mês 13 ele recusa; dia 31 de
 *   fevereiro, não). Numa tela, o usuário digita e VÊ a data que entrou; aqui
 *   quem escreve é o modelo, e a data trocada chegaria ao formulário sem
 *   ninguém ter pedido. Por isso o dia que sai tem de ser o dia que entrou —
 *   é uma guarda sobre a régua, não uma segunda régua.
 *
 * O formato de SAÍDA é o que muda por formulário: a tarefa guarda o ISO que
 * ela já devolve; o modal do CRM lê `DD/MM/YYYY`, e a conversão final passa
 * pelo `aawzDate` com formato explícito (ele não aceita lista de formatos).
 */
function comoData(valor, formatoDeSaida) {
  if (!ehTextoUtil(valor)) return null
  const texto = valor.trim()

  const iso = toIsoDate(texto)
  if (!iso) return null

  const pedido = diaPedido(texto)
  const devolvido = diaPedido(iso)
  if (!pedido || !devolvido) return null
  if (pedido.join('-') !== devolvido.join('-')) return null

  if (formatoDeSaida === FORMATO_DA_TAREFA) return iso
  const data = aawzDate(iso, FORMATO_DA_TAREFA)
  return data.isValid() ? data.format(formatoDeSaida) : null
}

/** Opção do vocabulário do módulo, sem exigir que o modelo acerte a caixa. */
function comoOpcao(valor, opcoes) {
  if (!ehTextoUtil(valor)) return null
  const alvo = valor.trim().toLowerCase()
  const achada = opcoes.filter((o) => String(o).toLowerCase() === alvo)
  return achada.length ? achada[0] : null
}

const texto = () => ({
  normalizar: (v) => (ehTextoUtil(v) ? v.trim() : null),
  espera: 'um texto',
})

const numero = () => ({
  normalizar: comoNumero,
  espera: 'um número',
})

const id = () => ({
  normalizar: (v) => {
    const n = comoNumero(v)
    return n !== null && n > 0 ? n : null
  },
  espera: 'o identificador numérico do registro',
})

const data = (formatoDeSaida) => ({
  normalizar: (v) => comoData(v, formatoDeSaida),
  espera: 'uma data existente, em AAAA-MM-DD',
})

const opcao = (opcoes) => ({
  normalizar: (v) => comoOpcao(v, opcoes),
  espera: 'um destes valores: ' + opcoes.join(', '),
})

/**
 * O contrato de `crm-opportunities-modal.vue`. A ordem é a da tela — ela é a
 * mesma de `campos_aplicados`, que o modelo lê para conferir o que entrou.
 */
export const CAMPOS_DA_NOVA_OPORTUNIDADE = {
  nome: texto(),
  valor: numero(),
  data_referencia: data(FORMATO_DO_CRM),
  tipo_oportunidade: opcao(OPPORTUNITY_TYPES),
  id_funil: id(),
  id_etapa: id(),
  id_cliente_app: id(),
  temperatura: opcao(TEMPERATURAS),
  observacao: texto(),
}

/**
 * O contrato de `_buildCreateModeTaskData`. `defaultStatus`, `externalId` e
 * `externalSource` ficam de FORA: os dois primeiros são fiação de quem abre o
 * modal (status da coluna do kanban, id do registro de origem) e o terceiro é
 * carimbado por este arquivo — o modelo não escolhe a procedência do registro.
 */
export const CAMPOS_DA_NOVA_TAREFA = {
  defaultTitle: texto(),
  defaultDescription: texto(),
  defaultStartDate: data(FORMATO_DA_TAREFA),
  defaultDueDate: data(FORMATO_DA_TAREFA),
  defaultClienteAppId: id(),
  defaultResponsavelProfissional: {
    normalizar: comoResponsavel,
    espera: 'o identificador do profissional responsável',
  },
  folderId: id(),
  sprintId: id(),
}

/**
 * O responsável chega como id e sai no shape que o formulário monta
 * (`responsavelPrincipal: [responsavel]`). O molde é
 * `resolveResponsavelFromConversa` (`atendimento/utils/task-from-conversa.js`),
 * o único produtor real desse objeto hoje — repassar o número cru faria o
 * picker receber `[123]` e desenhar vazio, sem erro nenhum.
 */
function comoResponsavel(valor) {
  const bruto = valor && typeof valor === 'object' ? valor.id : valor
  const idProfissional = comoNumero(bruto)
  if (idProfissional === null || idProfissional <= 0) return null
  const nome = valor && typeof valor === 'object' ? valor.nome : null
  return {
    id: idProfissional,
    nome: ehTextoUtil(nome) ? nome.trim() : '',
    tipo: 'profissional',
  }
}

/**
 * Valida `campos` contra um contrato e devolve o veredito no shape que o
 * `preencher` espera — `{valido, erros, campos}`, com `campos` já normalizado e
 * NA ORDEM DO CONTRATO (independente da ordem em que o modelo escreveu).
 */
function validarContra(contrato, nomeDoFormulario) {
  const aceitos = Object.keys(contrato)

  return (campos) => {
    const entrada = campos && typeof campos === 'object' ? campos : {}
    const erros = []

    Object.keys(entrada).forEach((campo) => {
      if (aceitos.indexOf(campo) === -1) {
        erros.push(
          'o campo "' + campo + '" não existe no formulário "' +
            nomeDoFormulario + '"; ele aceita: ' + aceitos.join(', ') + '.'
        )
      }
    })

    const normalizados = {}
    aceitos.forEach((campo) => {
      if (!Object.prototype.hasOwnProperty.call(entrada, campo)) return
      const valor = contrato[campo].normalizar(entrada[campo])
      if (valor === null) {
        erros.push(
          'o campo "' + campo + '" espera ' + contrato[campo].espera + '.'
        )
        return
      }
      normalizados[campo] = valor
    })

    if (erros.length) return { valido: false, erros: erros, campos: {} }
    if (!Object.keys(normalizados).length) {
      return {
        valido: false,
        erros: ['nenhum campo conhecido do formulário "' + nomeDoFormulario + '".'],
        campos: {},
      }
    }
    return { valido: true, erros: [], campos: normalizados }
  }
}

const camposAplicados = (veredito) => Object.keys(veredito.campos)

export const formularioNovaOportunidade = {
  nome: FORMULARIO_NOVA_OPORTUNIDADE,
  canal: CANAL_GLOBALBUS,
  evento: MODAL.CRM_OPPORTUNITIES,
  validar: validarContra(CAMPOS_DA_NOVA_OPORTUNIDADE, FORMULARIO_NOVA_OPORTUNIDADE),
  /**
   * `allowEditClient: true` é deliberado: com `id_cliente_app` preenchido, o
   * modal marca `wasOpenWithClient` e ESCONDE o seletor de cliente
   * (`canShowClientOptions`, `crm-opportunities-modal.vue:71-76`) — o consultor
   * não veria de quem é a oportunidade que a IA montou, nem poderia corrigir um
   * cliente escolhido errado. A tool existe para ele conferir antes de salvar.
   */
  montarPayload: (veredito) => ({
    item: { ...veredito.campos },
    allowEditClient: true,
  }),
  camposAplicados: camposAplicados,
}

export const formularioNovaTarefa = {
  nome: FORMULARIO_NOVA_TAREFA,
  canal: CANAL_GLOBALBUS,
  evento: MODAL.TASK_DETAIL,
  validar: validarContra(CAMPOS_DA_NOVA_TAREFA, FORMULARIO_NOVA_TAREFA),
  // Os campos ausentes NÃO viram `null` no payload: `_buildCreateModeTaskData`
  // já aplica o default de cada um (`p.defaultTitle || ''`), e mandar a chave
  // vazia só ocuparia espaço no que o consultor vê no `tool_output`.
  montarPayload: (veredito) => ({
    ...veredito.campos,
    externalSource: ORIGEM_DA_IA,
  }),
  camposAplicados: camposAplicados,
}

export const FORMULARIOS_GLOBALBUS = [
  formularioNovaOportunidade,
  formularioNovaTarefa,
]

/**
 * Liga os dois formulários ao `preencher`. Chamada por
 * `tools-de-front/index.js`, que só é carregado no BOOT (por
 * `ui-registry/plataforma.js`) — o mesmo caminho, e o mesmo motivo, pelo qual o
 * catálogo de navegação não encosta no grafo do `apiChat`.
 *
 * Idempotente pela consulta ao REGISTRO, e não por flag da função: assim ela
 * continua verdadeira depois de um `limparFormularios()` do teste.
 */
export function registrarFormulariosGlobalBus() {
  FORMULARIOS_GLOBALBUS.forEach((formulario) => {
    if (formularioRegistrado(formulario.nome)) return
    registrarFormulario(formulario)
  })
}

export default FORMULARIOS_GLOBALBUS
